import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SectionHeader from '../ui/SectionHeader';
import BookSpotlightCard from '../books/BookSpotlightCard';

const ROTATE_MS = 6000;
const PAGE_SIZE = 2;

function BannerSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-xl3 border border-slate-100 dark:border-white/10 bg-white dark:bg-surface-darkCard p-6 sm:p-8">
      <div className="grid gap-8 sm:grid-cols-2 sm:divide-x sm:divide-slate-100 dark:sm:divide-white/10">
        {[0, 1].map((i) => (
          <div key={i} className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6 sm:first:pr-2 sm:last:pl-8">
            <div className="mx-auto aspect-[2/3] w-28 shrink-0 skeleton rounded-xl2 sm:mx-0 sm:w-36" />
            <div className="flex w-full flex-col items-center gap-3 sm:items-start">
              <div className="h-4 w-24 skeleton rounded" />
              <div className="h-6 w-3/4 skeleton rounded" />
              <div className="h-4 w-1/2 skeleton rounded" />
              <div className="h-9 w-32 skeleton rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 100% hand-curated (Editor's Pick admin selection) per book_category_logic.txt:
// a rotating banner instead of a scrollable carousel, since these are meant
// to be a handful of spotlighted picks, not a browsable list. Shown two
// books at a time, side by side; auto-advances on a timer but the row is
// also a real scroll-snap container so the user can drag/swipe through it
// manually — either way, `page` stays the single source of truth for which
// pair is showing.
export default function FeaturedBanner({ books, loading }) {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef(null);
  const scrollEndTimer = useRef(null);
  const initialized = useRef(false);

  const pages = [];
  for (let i = 0; i < books.length; i += PAGE_SIZE) pages.push(books.slice(i, i + PAGE_SIZE));
  // An odd book count leaves the last page with just one book — pad it by
  // wrapping in from the front instead, so every page always shows a pair.
  // That book plays double duty (its own page, plus filler here); nothing
  // gets dropped from the rotation to force an even count.
  if (pages.length > 1) {
    const last = pages[pages.length - 1];
    if (last.length < PAGE_SIZE) pages[pages.length - 1] = [...last, ...books.slice(0, PAGE_SIZE - last.length)];
  }
  const pageCount = pages.length;
  // A clone of the last page before the real pages, and a clone of the first
  // page after them, so the row always has somewhere to scroll *into* no
  // matter which direction — forward past the end or backward past the
  // start — instead of hitting a hard boundary. `page` indexes into this
  // array, offset by 1 (index 0 is the leading clone), and can briefly sit
  // on either clone before handleScroll snaps it back into the real range.
  const extendedPages = pageCount > 1 ? [pages[pageCount - 1], ...pages, pages[0]] : pages;
  const offset = pageCount > 1 ? 1 : 0;
  // Real 0-based page, for dots/divider — wraps `page` back into [0, pageCount).
  const safePage = pageCount ? (((page - offset) % pageCount) + pageCount) % pageCount : 0;

  // Position on the real first page before the first paint, with no visible
  // scroll — otherwise the row would start on the leading clone (scrollLeft
  // defaults to 0) and jump forward the instant this ran. useLayoutEffect
  // (not useEffect) so it happens before the browser paints that first frame.
  useLayoutEffect(() => {
    if (pageCount <= 1 || initialized.current) return;
    initialized.current = true;
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.clientWidth;
    setPage(1);
  }, [pageCount]);

  useEffect(() => {
    if (paused || pageCount < 2) return;
    const timer = setInterval(() => {
      setPage((p) => p + 1);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [paused, pageCount]);

  // Scroll the row to match `page`, whether it changed via the timer or a
  // dot click. Always smooth — the leading/trailing clones mean this never
  // has to jump across the whole row to wrap around.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: page * el.clientWidth, behavior: 'smooth' });
  }, [page]);

  // Derive the nearest snapped page from scroll position, for both manual
  // drag/swipe and the programmatic scroll above, so dots and the
  // auto-rotate timer stay in sync with wherever the row actually is. Waits
  // for scroll events to go quiet instead of assuming a fixed animation
  // duration — reading scrollLeft while a smooth scroll is still in flight
  // (native smooth-scroll timing varies by browser/device) was landing on a
  // partial position and making the row look like it stalled mid-transition.
  //
  // Landing on either clone snaps straight to the matching real page with no
  // animation — invisible, since each clone is pixel-identical to the real
  // page it mirrors — so scrolling can keep going in the same direction
  // forever instead of hitting the end of the row.
  const handleScroll = () => {
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(() => {
      const el = scrollRef.current;
      if (!el || !el.clientWidth) return;
      const nearest = Math.round(el.scrollLeft / el.clientWidth);
      if (pageCount > 1 && nearest === 0) {
        el.scrollLeft = pageCount * el.clientWidth;
        setPage(pageCount);
      } else if (pageCount > 1 && nearest === pageCount + 1) {
        el.scrollLeft = el.clientWidth;
        setPage(1);
      } else {
        setPage((p) => (nearest !== p ? nearest : p));
      }
    }, 120);
  };

  useEffect(() => () => {
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
  }, []);

  if (!loading && books.length === 0) return null;

  return (
    <section className="container-app py-10">
      <SectionHeader title="Featured Books" subtitle="Curated by our editorial team this month." to="/shop?q=featured" />

      {loading ? (
        <BannerSkeleton />
      ) : (
        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
          className="relative overflow-hidden rounded-xl3 border border-primary-100 dark:border-white/10 bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-primary-900/20 dark:via-surface-darkCard dark:to-accent-500/10"
        >
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="hide-scrollbar flex snap-x snap-mandatory overflow-x-auto scroll-smooth"
          >
            {extendedPages.map((group, i) => (
              <div
                key={i}
                className={`grid w-full shrink-0 snap-start gap-4 sm:gap-0 ${
                  group.length > 1 ? 'sm:grid-cols-2' : 'sm:mx-auto sm:max-w-lg'
                }`}
              >
                {group.map((book) => (
                  <BookSpotlightCard key={`${i}-${book.id}`} book={book} eyebrow="Editor's Pick" />
                ))}
              </div>
            ))}
          </div>

          {/* A fixed overlay rather than a per-page border: the border used
              to live inside each scrolling page, so it moved with the content
              during transitions and vanished on a trailing page with a single
              book. Pinning it to the wrapper keeps it planted dead-center
              regardless of scroll position or page content. */}
          {pages[safePage]?.length > 1 && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 bg-primary-100 sm:block dark:bg-white/10"
            />
          )}

          {pageCount > 1 && (
            <div className="relative z-10 flex items-center justify-center gap-2 pb-6">
              {pages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + offset)}
                  aria-label={`Show featured books ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === safePage ? 'w-6 bg-primary-600 dark:bg-primary-300' : 'w-1.5 bg-primary-200 dark:bg-white/20'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
